import React, { useState, useEffect } from "react";
import Select from "react-select";

const MultiRoleSelectForm = ({
  name,
  user,
  handleRoleChange,
  employee = false,
}) => {
  // Parse the roles from the input string (comma-separated role values)
  // Define complete roles data with all properties
  const all = [
    { id: 1, value: "ROLE_DI_ADMIN", label: "DI Admin" },
    { id: 2, value: "ROLE_ADMIN", label: "Admin" },
    { id: 3, value: "ROLE_HOSPITAL_ADMIN", label: "Hospital Admin" },
    { id: 4, value: "ROLE_USER", label: "User" },
    { id: 5, value: "ROLE_DOCTOR", label: "Doctor" },
    { id: 10, value: "SUPER_ADMIN", label: "Super Admin" },
  ];

  const Employee = [
    { id: 6, value: "ROLE_LAB", label: "Lab" },
    { id: 7, value: "ROLE_BLOGGER", label: "Blogger" },
    { id: 8, value: "ROLE_RECEPTION", label: "Reception" },
    { id: 9, value: "ROLE_HR", label: "HR" },
  ];

  const roleOptions = employee ? Employee : [...Employee, ...all];

  const parseRoles = (userRoles) => {
    if (!userRoles) return [];

    let parsed = [];

    // Handle arrays (mixed objects/strings/numbers)
    if (Array.isArray(userRoles)) {
      parsed = userRoles
        .map((role) => {
          // If already a role object, validate existence
          if (typeof role === "object") {
            return roleOptions.find((opt) => opt.id === role.id) || role;
          }

          // Handle string/number role identifiers
          const strRole = String(role).trim();

          // 1. Try to find by value (ROLE_ADMIN)
          const byValue = roleOptions.find((opt) => opt.value === strRole);
          if (byValue) return byValue;

          // 2. Try to find by ID (numeric check)
          const id = parseInt(strRole, 10);
          if (!isNaN(id)) {
            return roleOptions.find((opt) => opt.id === id);
          }

          return undefined; // Invalid entries filtered out
        })
        .filter(Boolean);
    }
    // Handle string input (comma-separated values/IDs)
    else {
      parsed = userRoles
        .split(",")
        .map((role) => role.trim())
        .map((roleValue) => {
          // 1. Try value match first
          const byValue = roleOptions.find((opt) => opt.value === roleValue);
          if (byValue) return byValue;

          // 2. Try numeric ID match
          const id = parseInt(roleValue, 10);
          if (!isNaN(id)) {
            return roleOptions.find((opt) => opt.id === id);
          }

          return undefined; // Invalid entries filtered out
        })
        .filter(Boolean);
    }

    // Remove duplicates by ID
    return parsed.filter(
      (role, index, self) => self.findIndex((r) => r.id === role.id) === index
    );
  };

  // Initialize state with parsed roles
  const [selectedRoles, setSelectedRoles] = useState([]);

  // Update selected roles whenever user prop changes
  useEffect(() => {
    const parsedRoles = parseRoles(user);
    setSelectedRoles(parsedRoles);
  }, [user]);
  //   }, [user]);

  // Handle role selection changes
  const onRoleChange = (selectedOptions) => {
    const newSelectedRoles = selectedOptions || [];
    setSelectedRoles(newSelectedRoles);

    if (handleRoleChange) {
      // Create a formatted string of IDs (1,2,3)
      const roleIds = newSelectedRoles.map((option) => option.id).join(",");

      // Create the role values string (ROLE_ADMIN,ROLE_USER,etc)
      const roleValues = newSelectedRoles
        .map((option) => option.value)
        .join(",");

      // Create the role codes string (admin,user,etc)
      const roleCodes = newSelectedRoles.map((option) => option.code).join(",");

      // Create the custom event with all necessary data
      const formattedData = {
        target: {
          name: name,
          value: roleIds, // Primary value is the IDs (1,2,3)
          roleValues: roleValues, // ROLE_ADMIN,ROLE_USER,etc
          roleCodes: roleCodes, // admin,user,etc
          fullData: newSelectedRoles, // Complete role objects
        },
      };

      // Call the handler with the formatted data
      handleRoleChange(formattedData);
    }
  };

  // Custom styles for the select component
  const customStyles = {
    control: (provided) => ({
      ...provided,
      borderRadius: "0.375rem",
      borderColor: "#ced4da",
      minHeight: "38px",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#ced4da",
      },
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#e9ecef",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#495057",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "#495057",
      "&:hover": {
        backgroundColor: "#dee2e6",
        color: "#000",
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#0d6efd"
        : state.isFocused
        ? "#e9ecef"
        : null,
      color: state.isSelected ? "white" : "black",
    }),
  };

  return (
    <div className="col">
      <label htmlFor="roleSelect" className="form-label">
        ROLES
      </label>
      <Select
        isMulti
        id="roleSelect"
        name={name}
        options={roleOptions}
        value={selectedRoles}
        onChange={onRoleChange}
        placeholder="Select roles"
        isClearable
        styles={customStyles}
        className="basic-multi-select"
        classNamePrefix="select"
        closeMenuOnSelect={false}
      />

      {/* Hidden fields for form submission */}
      <input
        type="hidden"
        name={name}
        value={selectedRoles.map((role) => role.id).join(",")}
      />
      <input
        type="hidden"
        name={name}
        value={selectedRoles.map((role) => role.value).join(",")}
      />
      <input
        type="hidden"
        name={name}
        value={selectedRoles.map((role) => role.code).join(",")}
      />

      {/* Debug information - can be removed in production */}
      <div className="mt-2 small text-muted">
        <strong>Selected Roles:</strong>{" "}
        {selectedRoles.map((r) => r.label).join(", ")}
        <br />
        {/* <strong>Role IDs:</strong> {selectedRoles.map((r) => r.id).join(",")} */}
        <strong> Assigned Role:</strong> {user}
      </div>
    </div>
  );
};

export default MultiRoleSelectForm;
